import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  PanelConfig,
  PanelId,
  PanelState,
  PanelPosition,
  PanelBounds,
} from '../../core/types/panel.types';
import { DEFAULT_PANEL_CONFIGS } from '../constants/panels';
import { getNextZIndex, initZIndexCounter } from '../utils/z-index-counter';

// Animation types for panel transitions
type PanelAnimationType = 'opening' | 'closing' | 'minimizing' | 'restoring' | null;

interface PanelWithZIndex extends PanelConfig {
  zIndex?: number;
  animating?: PanelAnimationType;
}

interface PanelStore {
  panels: Record<PanelId, PanelWithZIndex>;
  activePanelId: PanelId | null;
  previousStates: Record<PanelId, PanelState>; // Track state before minimizing

  // Panel Actions
  setActivePanel: (id: PanelId) => void;
  bringToFront: (id: PanelId) => void;
  togglePanel: (id: PanelId) => void;
  showPanel: (id: PanelId) => void;
  hidePanel: (id: PanelId) => void;
  closePanel: (id: PanelId) => void;

  // Panel State
  setPanelState: (id: PanelId, state: PanelState) => void;
  minimizePanel: (id: PanelId) => void;
  maximizePanel: (id: PanelId) => void;
  restorePanel: (id: PanelId) => void;
  floatPanel: (id: PanelId, bounds?: PanelBounds) => void;
  dockPanel: (id: PanelId, position: PanelPosition) => void;

  // Panel Resize
  resizePanel: (id: PanelId, width: number, height: number) => void;
  setPanelBounds: (id: PanelId, bounds: PanelBounds) => void;

  // Layout
  resetToDefault: () => void;
  getPanelsByPosition: (position: PanelPosition) => PanelConfig[];
  getVisiblePanels: () => PanelConfig[];

  // Window resize handling
  clampFloatingPanelsToViewport: () => void;

  // Animation
  setPanelAnimating: (id: PanelId, animating: PanelAnimationType) => void;
  clearPanelAnimation: (id: PanelId) => void;
}

// Constants for floating panel bounds
const TOP_OFFSET = 32; // MenuBar height
const HEADER_HEIGHT = 32; // Panel header height (h-8 = 2rem = 32px)
const MIN_VISIBLE_WIDTH = 70; // Minimum visible panel width on screen

/**
 * Clamp panel bounds to keep panel accessible.
 * - Top: constrained to protect the menubar/toolbar area
 * - Bottom: constrained so title bar remains visible for dragging
 * - Left/Right: constrained so at least MIN_VISIBLE_WIDTH remains on screen
 */
const clampPanelBounds = (bounds: PanelBounds, minWidth = 200, minHeight = 150): PanelBounds => {
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 768;
  const maxY = viewportHeight - HEADER_HEIGHT;
  const minX = -bounds.width + MIN_VISIBLE_WIDTH;
  const maxX = viewportWidth - MIN_VISIBLE_WIDTH;
  return {
    ...bounds,
    width: Math.max(minWidth, bounds.width),
    height: Math.max(minHeight, bounds.height),
    x: Math.max(minX, Math.min(maxX, bounds.x)), // Left and right constraint
    y: Math.max(TOP_OFFSET, Math.min(maxY, bounds.y)), // Top and bottom constraint
  };
};

// Deep clone helper
const clonePanels = (panels: Record<PanelId, PanelWithZIndex>): Record<PanelId, PanelWithZIndex> => {
  const result = {} as Record<PanelId, PanelWithZIndex>;
  for (const key of Object.keys(panels) as PanelId[]) {
    result[key] = {
      ...panels[key],
      bounds: panels[key].bounds ? { ...panels[key].bounds } : undefined
    };
  }
  return result;
};

export const usePanelStore = create<PanelStore>()(
  persist(
    (set, get) => ({
      panels: clonePanels(DEFAULT_PANEL_CONFIGS),
      activePanelId: 'scene-view' as PanelId,
      previousStates: {} as Record<PanelId, PanelState>,

      setActivePanel: (id) => {
        const panel = get().panels[id];
        // Automatically bring floating panels to front when activated
        if (panel.state === 'floating') {
          const newZIndex = getNextZIndex();
          const panels = clonePanels(get().panels);
          panels[id].zIndex = newZIndex;
          set({
            activePanelId: id,
            panels,
          });
        } else {
          set({ activePanelId: id });
        }
      },

      bringToFront: (id) => {
        const panel = get().panels[id];
        if (panel.state === 'floating') {
          const newZIndex = getNextZIndex();
          const panels = clonePanels(get().panels);
          panels[id].zIndex = newZIndex;
          set({
            panels,
            activePanelId: id
          });
        }
      },

      togglePanel: (id) => {
        const panel = get().panels[id];
        if (!panel) return;
        if (panel.visible) {
          // Closing - animate first, then hide
          const panels = clonePanels(get().panels);
          panels[id].animating = 'closing';
          set({ panels });
          setTimeout(() => {
            const panels = clonePanels(get().panels);
            panels[id].visible = false;
            panels[id].animating = null;
            set({ panels });
          }, 100);
        } else {
          // Opening - show with animation
          const panels = clonePanels(get().panels);
          panels[id].visible = true;
          panels[id].animating = 'opening';
          // Ensure floating-position panels have floating state so they render correctly
          if (panels[id].position === 'floating' && panels[id].state === 'normal') {
            panels[id].state = 'floating';
          }
          set({ panels });
          setTimeout(() => {
            const panels = clonePanels(get().panels);
            panels[id].animating = null;
            set({ panels });
          }, 100);
        }
      },

      showPanel: (id) => {
        if (!get().panels[id]) return;
        const panels = clonePanels(get().panels);
        panels[id].visible = true;
        panels[id].animating = 'opening';
        // Ensure floating-position panels have floating state so they render correctly
        if (panels[id].position === 'floating' && panels[id].state === 'normal') {
          panels[id].state = 'floating';
        }
        set({ panels });
        // Clear animation after it completes
        setTimeout(() => {
          const panels = clonePanels(get().panels);
          panels[id].animating = null;
          set({ panels });
        }, 100);
      },

      hidePanel: (id) => {
        if (!get().panels[id]) return;
        const panels = clonePanels(get().panels);
        panels[id].animating = 'closing';
        set({ panels });
        // Actually hide after animation completes
        setTimeout(() => {
          const panels = clonePanels(get().panels);
          panels[id].visible = false;
          panels[id].animating = null;
          set({ panels });
        }, 100);
      },

      closePanel: (id) => {
        const panel = get().panels[id];
        if (!panel) return;
        if (panel.closeable) {
          const panels = clonePanels(get().panels);
          panels[id].animating = 'closing';
          set({ panels });
          // Actually hide after animation completes
          setTimeout(() => {
            const panels = clonePanels(get().panels);
            panels[id].visible = false;
            panels[id].animating = null;
            set({ panels });
          }, 100);
        }
      },

      setPanelState: (id, panelState) => {
        const panels = clonePanels(get().panels);
        panels[id].state = panelState;
        set({ panels });
      },

      minimizePanel: (id) => {
        const panel = get().panels[id];
        if (panel.minimizable) {
          // Save the current state before minimizing
          const currentState = panel.state;
          const panels = clonePanels(get().panels);
          panels[id].animating = 'minimizing';
          set({
            panels,
            previousStates: {
              ...get().previousStates,
              [id]: currentState,
            },
          });
          // Actually minimize after animation completes
          setTimeout(() => {
            const panels = clonePanels(get().panels);
            panels[id].state = 'minimized';
            panels[id].animating = null;
            set({ panels });
          }, 100);
        }
      },

      maximizePanel: (id) => {
        const panel = get().panels[id];
        if (panel.maximizable) {
          const panels = clonePanels(get().panels);
          panels[id].state = 'maximized';
          panels[id].animating = 'opening';
          set({ panels });
          // Clear animation after it completes
          setTimeout(() => {
            const panels = clonePanels(get().panels);
            panels[id].animating = null;
            set({ panels });
          }, 100);
        }
      },

      restorePanel: (id) => {
        const panels = clonePanels(get().panels);
        // Restore to previous state (floating or normal), default to 'normal' if not tracked
        let previousState = get().previousStates[id] || 'normal';
        // Ensure floating-position panels restore to floating state
        if (panels[id].position === 'floating' && previousState === 'normal') {
          previousState = 'floating';
        }
        panels[id].state = previousState;
        panels[id].animating = 'restoring';
        set({ panels });
        // Clear animation after it completes
        setTimeout(() => {
          const panels = clonePanels(get().panels);
          panels[id].animating = null;
          set({ panels });
        }, 100);
      },

      floatPanel: (id, bounds) => {
        const newZIndex = getNextZIndex();
        const panels = clonePanels(get().panels);
        const panel = panels[id];
        panels[id].state = 'floating';
        panels[id].zIndex = newZIndex; // Give it the highest z-index
        panels[id].animating = 'opening';
        if (bounds) {
          // Clamp provided bounds to ensure they are within valid area
          panels[id].bounds = clampPanelBounds(bounds, panel.minWidth, panel.minHeight);
        } else {
          // Center the panel with slight offset based on z-index to cascade
          const offset = (newZIndex % 10) * 20;
          const initialBounds: PanelBounds = {
            x: window.innerWidth / 2 - panel.defaultWidth / 2 + offset,
            y: window.innerHeight / 2 - panel.defaultHeight / 2 + offset,
            width: panel.defaultWidth,
            height: panel.defaultHeight,
          };
          // Clamp to ensure panel starts in a valid position
          panels[id].bounds = clampPanelBounds(initialBounds, panel.minWidth, panel.minHeight);
        }
        set({ panels, activePanelId: id });
        // Clear animation after it completes
        setTimeout(() => {
          const panels = clonePanels(get().panels);
          panels[id].animating = null;
          set({ panels });
        }, 100);
      },

      dockPanel: (id, position) => {
        const panels = clonePanels(get().panels);
        panels[id].state = 'normal';
        // If position is 'floating', dock to 'bottom' as a sensible default
        panels[id].position = position === 'floating' ? 'bottom' : position;
        panels[id].bounds = undefined;
        panels[id].zIndex = undefined;
        panels[id].animating = 'opening';
        set({ panels });
        // Clear animation after it completes
        setTimeout(() => {
          const panels = clonePanels(get().panels);
          panels[id].animating = null;
          set({ panels });
        }, 100);
      },

      resizePanel: (id, width, height) => {
        const panels = clonePanels(get().panels);
        panels[id].defaultWidth = Math.max(panels[id].minWidth, width);
        panels[id].defaultHeight = Math.max(panels[id].minHeight, height);
        if (panels[id].bounds) {
          panels[id].bounds!.width = panels[id].defaultWidth;
          panels[id].bounds!.height = panels[id].defaultHeight;
        }
        set({ panels });
      },

      setPanelBounds: (id, bounds) => {
        const panels = clonePanels(get().panels);
        panels[id].bounds = bounds;
        set({ panels });
      },

      resetToDefault: () => {
        set({
          panels: clonePanels(DEFAULT_PANEL_CONFIGS),
          activePanelId: 'scene-view' as PanelId,
          previousStates: {} as Record<PanelId, PanelState>,
        });
      },

      getPanelsByPosition: (position) => {
        const { panels } = get();
        return Object.values(panels)
          .filter((p) => p.position === position && p.visible && p.state !== 'floating' && p.state !== 'minimized')
          .sort((a, b) => a.order - b.order);
      },

      getVisiblePanels: () => {
        const { panels } = get();
        return Object.values(panels).filter((p) => p.visible);
      },

      clampFloatingPanelsToViewport: () => {
        const { panels } = get();
        let hasChanges = false;
        const updatedPanels = clonePanels(panels);

        for (const id of Object.keys(updatedPanels) as PanelId[]) {
          const panel = updatedPanels[id];
          if (panel.state === 'floating' && panel.visible && panel.bounds) {
            const oldBounds = panel.bounds;
            // Use the same clamping logic as dragging
            const newBounds = clampPanelBounds(oldBounds, panel.minWidth, panel.minHeight);

            // Check if anything changed
            if (
              newBounds.x !== oldBounds.x ||
              newBounds.y !== oldBounds.y ||
              newBounds.width !== oldBounds.width ||
              newBounds.height !== oldBounds.height
            ) {
              hasChanges = true;
              updatedPanels[id].bounds = newBounds;
            }
          }
        }

        if (hasChanges) {
          set({ panels: updatedPanels });
        }
      },

      setPanelAnimating: (id, animating) => {
        const panels = clonePanels(get().panels);
        panels[id].animating = animating;
        set({ panels });
      },

      clearPanelAnimation: (id) => {
        const panels = clonePanels(get().panels);
        panels[id].animating = null;
        set({ panels });
      },
    }),
    {
      name: 'panel-layout',
      partialize: (state) => ({
        panels: state.panels,
        activePanelId: state.activePanelId,
        previousStates: state.previousStates,
      }),
      merge: (persistedState: unknown, currentState: PanelStore) => {
        const persisted = persistedState as Partial<PanelStore> | undefined;
        if (!persisted || !persisted.panels) {
          return currentState;
        }
        // Merge persisted panels with defaults so newly added panels are included
        const mergedPanels = { ...clonePanels(DEFAULT_PANEL_CONFIGS) };
        for (const key of Object.keys(persisted.panels) as PanelId[]) {
          if (persisted.panels[key]) {
            mergedPanels[key] = { ...mergedPanels[key], ...persisted.panels[key] };
            // Fix: ensure floating-position panels have correct state
            // (stale persisted state may have state:'normal' for floating panels)
            if (mergedPanels[key].position === 'floating' && mergedPanels[key].state === 'normal') {
              mergedPanels[key].state = 'floating';
            }
          }
        }
        return {
          ...currentState,
          ...persisted,
          panels: mergedPanels,
        };
      },
      onRehydrateStorage: () => (state) => {
        // After state is rehydrated from localStorage, sync the shared z-index counter
        // with persisted panel z-indexes so new panels get higher values
        if (state) {
          let maxZ = 50;
          for (const p of Object.values(state.panels)) {
            if (p.zIndex && p.zIndex > maxZ) maxZ = p.zIndex;
          }
          initZIndexCounter(maxZ);
          // Use setTimeout to ensure window dimensions are ready
          setTimeout(() => {
            state.clampFloatingPanelsToViewport();
          }, 0);
        }
      },
    }
  )
);
